import type React from "react";
import { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { List, IconButton, Collapse, Box, useTheme } from "@mui/material";
import {
  KeyboardArrowDown as ArrowDownIcon,
  Build as DevToolsIcon,
  CloudUpload as DataImporterIcon,
  MergeType as DedupeIcon,
} from "@mui/icons-material";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import type translations from "../../translations/translations.json";
import SidebarMenuItem from "../SidebarMenuItem";
import { rawColor } from "../../constants/colors";
import { TestId } from "../../constants/testIds";

type TranslationKeys = keyof typeof translations.en;

interface DevToolsMenuProps {
  onNavigate?: () => void;
}

const DevToolsMenu: React.FC<DevToolsMenuProps> = ({ onNavigate }) => {
  const location = useLocation();
  const { t } = useTranslation();
  const theme = useTheme();
  const manuallyCollapsedRef = useRef(false);
  const manuallyExpandedRef = useRef(false);

  const isDevToolsPage = useMemo(() => {
    return location.pathname.startsWith("/dev-tools");
  }, [location.pathname]);

  const [expanded, setExpanded] = useState(() => isDevToolsPage);

  useEffect(() => {
    if (isDevToolsPage) {
      if (manuallyExpandedRef.current) {
        setExpanded(true);
      } else if (manuallyCollapsedRef.current) {
        setExpanded(false);
      } else {
        setExpanded(true);
      }
    } else {
      setExpanded(false);
      manuallyExpandedRef.current = false;
      manuallyCollapsedRef.current = false;
    }
  }, [isDevToolsPage]);

  const toggle = useCallback(() => {
    setExpanded((prev) => {
      const next = !prev;
      if (next) {
        manuallyExpandedRef.current = true;
        manuallyCollapsedRef.current = false;
      } else {
        manuallyCollapsedRef.current = true;
        manuallyExpandedRef.current = false;
      }
      return next;
    });
  }, []);

  return (
    <Box sx={{ width: "100%" }}>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          width: "100%",
          position: "relative",
        }}
      >
        <SidebarMenuItem
          to="/dev-tools/data-importer"
          selected={isDevToolsPage}
          icon={<DevToolsIcon />}
          text={t("Dev Tools" as TranslationKeys)}
          sx={{ flex: 1 }}
          onNavigate={onNavigate}
          data-testid={TestId.SIDEBAR.MENU_ITEM_DEV_TOOLS}
        />
        <IconButton
          size="small"
          onClick={toggle}
          sx={{
            transform: expanded ? "rotate(180deg)" : "rotate(0deg)",
            transition: theme.transitions.create("transform", {
              duration: theme.transitions.duration.shorter,
            }),
            position: "absolute",
            right: 4,
            zIndex: 1,
            color: rawColor.white.transparent["07"],
            "&:hover": {
              backgroundColor: rawColor.white.transparent["12"],
            },
          }}
        >
          <ArrowDownIcon />
        </IconButton>
      </Box>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List component="div" disablePadding data-testid={TestId.SIDEBAR.DEV_TOOLS_MENU}>
          <SidebarMenuItem
            to="/dev-tools/data-importer"
            selected={location.pathname === "/dev-tools/data-importer"}
            icon={<DataImporterIcon fontSize="small" />}
            text={t("Data Importer" as TranslationKeys)}
            isSubmenu
            textProps={{ fontSize: "0.875rem" }}
            sx={{ pl: 4, py: 0.375, minHeight: "unset" }}
            onNavigate={onNavigate}
            data-testid={TestId.SIDEBAR.MENU_ITEM_DATA_IMPORTER}
          />
          <SidebarMenuItem
            to="/dev-tools/dedupe"
            selected={location.pathname.startsWith("/dev-tools/dedupe")}
            icon={<DedupeIcon fontSize="small" />}
            text={t("Deduplicate Entities" as TranslationKeys)}
            isSubmenu
            textProps={{ fontSize: "0.875rem" }}
            sx={{ pl: 4, py: 0.375, minHeight: "unset" }}
            onNavigate={onNavigate}
            data-testid={TestId.SIDEBAR.MENU_ITEM_DEDUPE_USERS}
          />
        </List>
      </Collapse>
    </Box>
  );
};

export default DevToolsMenu;
